import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Bell } from "lucide-react"
import { AnimatePresence, motion, useAnimate, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router"

import { api } from "@/api/cliente"
import { type Aviso, destinoDe, ListaDeAvisos } from "./ListaDeAvisos"

/**
 * El contador de avisos sin leer, y los últimos ocho al pulsarlo.
 *
 * ⚠ **Sondea un endpoint que no trae filas.** `/avisos/conteo` devuelve un número y
 * nada más: es lo que se pide cada minuto en todas las pantallas y de todos los
 * usuarios a la vez. La lista sólo se pide al abrir el panel, que es cuando alguien
 * va a leerla.
 *
 * ⚠ **Abrir el panel NO marca nada como leído.** Marcar por pasar el ratón hace que
 * la campanita se vacíe sola y que el aviso que importaba desaparezca sin haberse
 * leído. Se marca al pulsar un aviso, o con «marcar todos», que es un gesto
 * deliberado.
 */

const CADA_MINUTO = 60_000

export function Campanita() {
  const [abierto, setAbierto] = useState(false)
  const caja = useRef<HTMLDivElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  /** Bajo el botón, alineado a su borde derecho. Ver el portal más abajo. */
  const [ancla, setAncla] = useState<{ top: number; right: number; estrecha: boolean } | null>(null)
  const sinMovimiento = useReducedMotion()
  const clienteQuery = useQueryClient()
  const navegar = useNavigate()

  const conteo = useQuery({
    queryKey: ["avisos", "conteo"],
    queryFn: () => api.get<{ no_leidos: number }>("/api/v1/avisos/conteo"),
    refetchInterval: CADA_MINUTO,
  })

  const bandeja = useQuery({
    queryKey: ["avisos", "recientes"],
    queryFn: () => api.get<{ items: Aviso[] }>("/api/v1/avisos?limit=8"),
    // Nada que pedir mientras el panel está cerrado.
    enabled: abierto,
  })

  const marcar = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudo marcar el aviso como leído" },
    mutationFn: (id: number) => api.post(`/api/v1/avisos/${id}/leido`),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["avisos"] }),
  })

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      const objetivo = e.target as Node
      if (caja.current?.contains(objetivo) || panel.current?.contains(objetivo)) return
      setAbierto(false)
    }
    const escapar = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false)
    }
    // Girar el teléfono o redimensionar movería el botón y dejaría el panel colgando.
    const recolocar = () => setAbierto(false)
    window.addEventListener("resize", recolocar)
    document.addEventListener("mousedown", fuera)
    document.addEventListener("keydown", escapar)
    return () => {
      window.removeEventListener("resize", recolocar)
      document.removeEventListener("mousedown", fuera)
      document.removeEventListener("keydown", escapar)
    }
  }, [abierto])

  const sinLeer = conteo.data?.no_leidos ?? 0

  // ⚠ **Lo nuevo se ANUNCIA (12/10/2026).** Antes, un aviso que llegaba con el
  // sondeo sólo cambiaba el número y el `aria-label` del botón: nada lo decía. Ahora,
  // si sube la cuenta, la campana se mece una vez, la insignia late, sale una onda
  // dorada y la región viva de abajo lo dice. La primera lectura NO cuenta como
  // nuevo: al entrar no hay nada que anunciar, sólo lo pendiente.
  const anterior = useRef<number | null>(null)
  const [timbre, setTimbre] = useState(0)
  const [anuncio, setAnuncio] = useState("")
  const [campana, animarCampana] = useAnimate<HTMLSpanElement>()
  useEffect(() => {
    if (!conteo.isSuccess) return
    const antes = anterior.current
    anterior.current = sinLeer
    if (antes === null || sinLeer <= antes) return
    setTimbre((n) => n + 1)
    setAnuncio(
      sinLeer === 1 ? "Tienes un aviso nuevo." : `Tienes avisos nuevos: ${sinLeer} sin leer.`,
    )
    if (campana.current) {
      animarCampana(
        campana.current,
        { rotate: [0, 14, -11, 6, -3, 0] },
        { duration: 0.7, ease: "easeOut" },
      )
    }
  }, [sinLeer, conteo.isSuccess, campana, animarCampana])

  const abrir = (aviso: Aviso) => {
    marcar.mutate(aviso.id)
    setAbierto(false)
    // La tarea viaja en la URL para que el aviso sea un enlace de verdad: se puede
    // copiar, y volver atrás cierra la ficha en vez de salir de la pantalla.
    const destino = destinoDe(aviso)
    if (destino !== null) navegar(destino)
  }

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          setAncla({
            top: r.bottom + 8,
            right: window.innerWidth - r.right,
            estrecha: window.innerWidth < 640,
          })
          setAbierto((a) => !a)
        }}
        aria-expanded={abierto}
        aria-haspopup="menu"
        aria-label={sinLeer === 0 ? "Avisos" : `Avisos: ${sinLeer} sin leer`}
        // El realce con LUZ, como el resto del encabezado: este botón vive sobre el
        // cristal oscuro del marco, y `--surface-elevated` ahí es un parche claro. El
        // PANEL que despliega sí conserva la superficie del tema — es contenido que se
        // lee, no marco.
        className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] toque-cristal foco-cristal"
      >
        <span ref={campana} className="block origin-[50%_10%]">
          <Bell size={20} strokeWidth={1.5} aria-hidden="true" />
        </span>
        {sinLeer > 0 && (
          <motion.span
            key={timbre}
            initial={timbre === 0 ? false : { scale: 0.4 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 520, damping: 14 }}
            data-testid="avisos-sin-leer"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium tabular-nums"
            style={{ backgroundColor: "var(--destructive)", color: "var(--primary-fg)" }}
          >
            {sinLeer > 9 ? "9+" : sinLeer}
          </motion.span>
        )}
        {/* La onda dorada que sale UNA vez de la insignia. Con «reducir movimiento»
            no crece: sólo se desvanece. */}
        {timbre > 0 && (
          <motion.span
            key={`onda-${timbre}`}
            aria-hidden="true"
            className="pointer-events-none absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full shadow-[0_0_0_2px_rgb(var(--acceso-oro-claro)/0.8)]"
            initial={{ opacity: 0.9, scale: 1 }}
            animate={{ opacity: 0, scale: 2.6 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
        )}
      </button>
      {/* Existe SIEMPRE: una región viva que nace con el mensaje no se anuncia. */}
      <span className="sr-only" aria-live="polite">
        {anuncio}
      </span>

      {/* ⚠ **En un portal y `fixed`, como el panel de `Apariencia`.** El encabezado
          va dentro de un envoltorio con `overflow-hidden` —lo que le redondea las
          esquinas— y un panel `absolute` que asoma por debajo quedaba recortado. Y
          su ancho cede a la pantalla: 320 px fijos se salían por la izquierda de
          un teléfono pequeño. */}
      {createPortal(
        <AnimatePresence>
          {abierto && ancla && (
            <motion.div
              ref={panel}
              className={`fixed z-50 max-h-[calc(100dvh-5rem)] overflow-y-auto rounded-[var(--radius-tarjeta)] border p-2 ${ancla.estrecha ? "" : "w-[320px]"}`}
              // Por debajo de 640 px no caben 320 a la izquierda del botón: colgado
              // de él, el panel se salía por el borde izquierdo del teléfono. Ahí va
              // de borde a borde, con 8 px de margen.
              style={{
                top: ancla.top,
                right: ancla.estrecha ? 8 : Math.max(ancla.right, 8),
                left: ancla.estrecha ? 8 : undefined,
                backgroundColor: "var(--surface)",
                borderColor: "var(--border)",
                boxShadow: "var(--elev-3)",
              }}
              initial={sinMovimiento ? false : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: sinMovimiento ? 0 : 0.14 }}
            >
              <div className="mb-1 flex items-center justify-between px-1">
                <span className="text-xs font-medium uppercase tracking-wide">Avisos</span>
              </div>

              {bandeja.isPending && (
                <p role="status" className="p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
                  Cargando…
                </p>
              )}
              {bandeja.isError && (
                <p role="alert" className="p-3 text-sm" style={{ color: "var(--destructive)" }}>
                  No se pudieron cargar los avisos.
                </p>
              )}
              {bandeja.data && (
                <ListaDeAvisos avisos={bandeja.data.items} compacta onAbrir={abrir} />
              )}
              {/* (23/10) La bandeja completa ya no está en el menú: se entra desde aquí. */}
              <button
                type="button"
                className="boton-secundario mt-2 w-full px-3 py-2 text-sm"
                onClick={() => {
                  setAbierto(false)
                  navegar("/avisos")
                }}
              >
                Ver todos los avisos
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}
